/**
 * Parked Location: field + picker dialog (patrol logout)
 * ======================================================
 * Two pieces, deliberately separate:
 *
 *   ParkedLocationField   the row inside the logout form — a Select Location
 *                         button, or the chosen location with a Change button
 *   ParkedLocationDialog  the popup it opens, rendered as a sibling of the
 *                         logout dialog rather than nested inside it, so it
 *                         stands on its own and sizes to the viewport
 *
 * The dialog offers, in order:
 *
 *   Business  - the closest businesses to the car's last reported position.
 *               The nearest five show by default; a toggle reveals the rest.
 *               Picking one asks how the car sits relative to it — in front
 *               of, across from, near, or simply at it — since "parked at
 *               Kingston Pizza" and "parked across from Kingston Pizza" are
 *               different spots on the block.
 *   Address   - that position as a street address with cross streets,
 *               e.g. "321 Kingston Ave [President/Union]"
 *   Manual    - the original logout input, unchanged
 *
 * Everything is best effort. If the car has no Geotab link, the position is
 * missing, or a lookup fails, the dialog says so and manual entry still takes
 * whatever the unit types.
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MapPin, Building2, Navigation, Pencil, RefreshCw, AlertTriangle } from "lucide-react";

// The backend returns ten. The list shows this many at a time and the toggle
// swaps between pages, so it never grows past a screenful.
const BUSINESS_PAGE_SIZE = 5;

export function ParkedLocationField({ value, onPick }) {
  return (
    <div>
      <Label>Parked Location *</Label>
      {value ? (
        <div className="mt-1 flex items-center gap-2 p-2 rounded border border-slate-300 dark:border-slate-700 bg-slate-100 dark:bg-slate-800">
          <MapPin className="w-4 h-4 text-slate-500 dark:text-slate-400 flex-shrink-0" />
          <span className="flex-1 text-sm text-slate-800 dark:text-slate-200 break-words">{value}</span>
          <Button type="button" size="sm" variant="outline" onClick={onPick} className="h-6 text-[10px] px-2 flex-shrink-0">
            Change
          </Button>
        </div>
      ) : (
        <Button type="button" variant="outline" onClick={onPick} className="w-full mt-1">
          <MapPin className="w-4 h-4 mr-2" />Select Location
        </Button>
      )}
    </div>
  );
}

export function ParkedLocationDialog({ open, onOpenChange, carId, value, onChange }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  // Which page of businesses is showing. The nearest five carry almost every
  // logout; the rest are one tap away rather than a long scroll everyone pays
  // for, and swapping keeps the list a fixed height on a phone.
  const [businessPage, setBusinessPage] = useState(0);
  // The business waiting on a "how is it parked relative to this?" answer. Held
  // as a second view inside this same dialog — nesting one dialog in another is
  // exactly what this component was split out to avoid.
  const [pendingBusiness, setPendingBusiness] = useState(null);

  // Look up fresh each time it opens — the car may have moved since the last
  // time someone had this dialog up.
  useEffect(() => {
    if (!open || !carId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      setData(null);
      setBusinessPage(0);
      setPendingBusiness(null);
      try {
        const res = await axios.get(
          `${API}/patrol/cars/${carId}/location-suggestions`,
          { withCredentials: true }
        );
        if (!cancelled) setData(res.data);
      } catch (e) {
        if (!cancelled) setError(e.response?.data?.detail || "Could not look up the vehicle location");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, carId]);

  // A pick reports where it came from: a business or the vehicle's own
  // address both name a real point, and the card turns those into a map link.
  // Typing into the manual field below reports nothing, so it stays plain.
  const pick = (text, meta) => {
    if (!text || !text.trim()) return;
    onChange(text.trim(), meta);
    onOpenChange(false);
  };

  // How the car sits relative to a chosen business. "At" keeps the original
  // wording, so nothing changes for anyone who just wants the business named.
  const BUSINESS_RELATIONS = [
    { key: "at", label: "At", format: (v) => v },
    { key: "front", label: "In front of", format: (v) => `In front of ${v}` },
    { key: "across", label: "Across from", format: (v) => `Across from ${v}` },
    { key: "near", label: "Near", format: (v) => `Near ${v}` },
  ];

  const pickBusiness = (business, relation) => {
    setPendingBusiness(null);
    pick(relation.format(business.value), {
      source: "business",
      lat: business.latitude,
      lng: business.longitude,
    });
  };

  const ageLabel = (minutes) => {
    if (minutes === null || minutes === undefined) return "at an unknown time";
    if (minutes < 1) return "just now";
    if (minutes === 1) return "1 minute ago";
    if (minutes < 60) return `${minutes} minutes ago`;
    const hours = Math.floor(minutes / 60);
    return hours === 1 ? "over an hour ago" : `over ${hours} hours ago`;
  };

  // One suggestion row — same shape for a business and for the address.
  const suggestionButton = (key, onClick, primary, secondary) => (
    <button
      key={key}
      type="button"
      onClick={onClick}
      className="w-full text-left px-3 py-2 rounded border border-slate-300 dark:border-slate-700 bg-white dark:bg-slate-900 hover:border-blue-500 dark:hover:border-blue-500"
    >
      <span className="text-sm font-medium text-slate-800 dark:text-slate-200">{primary}</span>
      {secondary && <span className="block text-xs text-slate-500">{secondary}</span>}
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* Capped to the viewport with the body scrolling inside it: five
          businesses plus the address and manual entry is more than a phone
          screen holds, and without the cap it ran off the top and bottom. */}
      <DialogContent
        className="bg-slate-900 border-slate-800 max-h-[85vh] flex flex-col"
        // Don't hand focus to the manual input on open — on a phone that
        // throws the keyboard up over the suggestions the unit came for.
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>Select Parked Location</DialogTitle>
        </DialogHeader>

        {pendingBusiness ? (
          <div className="space-y-3 mt-2 flex-1 overflow-y-auto min-h-0 pr-1">
            <div>
              <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                <Building2 className="w-3 h-3" />Where is the car?
              </p>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-200">{pendingBusiness.name}</p>
              {pendingBusiness.address && (
                <p className="text-xs text-slate-500">{pendingBusiness.address}</p>
              )}
            </div>
            <div className="space-y-1">
              {BUSINESS_RELATIONS.map((relation) => suggestionButton(
                relation.key,
                () => pickBusiness(pendingBusiness, relation),
                relation.label,
                relation.format(pendingBusiness.value)
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={() => setPendingBusiness(null)}
              className="w-full"
            >
              Back
            </Button>
          </div>
        ) : (
        <div className="space-y-4 mt-2 flex-1 overflow-y-auto min-h-0 pr-1">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
              <Pencil className="w-3 h-3" />Manual entry
            </p>
            {/* The original logout field, unchanged: typing here writes
                straight through, with no extra step to confirm it. */}
            <Input
              value={value}
              onChange={(e) => onChange(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); if (value.trim()) onOpenChange(false); } }}
              placeholder="Enter where the car is parked"
              className="input-dark"
            />
          </div>

          {loading && (
            <p className="text-sm text-slate-600 dark:text-slate-400 flex items-center gap-2">
              <RefreshCw className="w-4 h-4 animate-spin" />
              Checking where the vehicle is...
            </p>
          )}

          {!loading && error && (
            <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
          )}

          {!loading && !error && data && !data.available && (
            <p className="text-sm text-slate-600 dark:text-slate-400">
              {data.reason || "No vehicle location available"} — type the location below.
            </p>
          )}

          {!loading && !error && data?.available && (
            <>
              <p className={`text-xs flex items-center gap-1 ${
                data.stale
                  ? "text-amber-700 dark:text-amber-400"
                  : "text-slate-600 dark:text-slate-400"
              }`}>
                {data.stale && <AlertTriangle className="w-3 h-3 flex-shrink-0" />}
                Vehicle reported {ageLabel(data.age_minutes)}
                {data.stale && " — it may have moved since"}
              </p>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                  <Building2 className="w-3 h-3" />Business
                </p>
                {data.businesses?.length ? (
                  <div className="space-y-1">
                    {data.businesses
                      .slice(businessPage * BUSINESS_PAGE_SIZE, (businessPage + 1) * BUSINESS_PAGE_SIZE)
                      .map((b, i) => suggestionButton(
                      `${b.name}-${businessPage}-${i}`,
                      () => setPendingBusiness(b),
                      b.name,
                      [
                        b.address,
                        b.distance_m !== null && b.distance_m !== undefined
                          ? `${Math.round(b.distance_m * 3.28084)} ft away`
                          : null,
                      ].filter(Boolean).join(" · ")
                    ))}
                    {data.businesses.length > BUSINESS_PAGE_SIZE && (
                      <button
                        type="button"
                        onClick={() => setBusinessPage((p) => (p === 0 ? 1 : 0))}
                        className="w-full text-center px-3 py-2 rounded border border-dashed border-slate-300 dark:border-slate-700 text-xs text-slate-600 dark:text-slate-400 hover:border-blue-500 dark:hover:border-blue-500"
                      >
                        {businessPage === 0
                          ? `Show ${Math.min(
                              data.businesses.length - BUSINESS_PAGE_SIZE,
                              BUSINESS_PAGE_SIZE
                            )} other businesses`
                          : "Show the closest 5"}
                      </button>
                    )}
                  </div>
                ) : (
                  <p className="text-xs text-slate-500 italic">No businesses found nearby</p>
                )}
              </div>

              <div>
                <p className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 flex items-center gap-1">
                  <Navigation className="w-3 h-3" />Address
                </p>
                {data.address
                  ? suggestionButton(
                      "address",
                      () => pick(data.address, { source: "address", lat: data.latitude, lng: data.longitude }),
                      data.address,
                      null
                    )
                  : <p className="text-xs text-slate-500 italic">Could not resolve an address</p>}
              </div>
            </>
          )}

        </div>
        )}

        {!pendingBusiness && (
          <div className="flex-shrink-0 pt-3">
            <Button type="button" onClick={() => onOpenChange(false)} disabled={!value.trim()} className="w-full btn-primary">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
